"use client"

import Image from "next/image"
import { motion } from "framer-motion"
import { useState } from "react"
import { Quote, Star } from "lucide-react"
import { testimonials } from "@/features/home/data"
import { staggerContainer, staggerItem, fadeUp, sectionLabel } from "@/lib/motion"

const VP = { once: true, amount: 0.05 }

function TestimonialAvatar({ testimonial }: { testimonial: typeof testimonials[0] }) {
  const [hasImage, setHasImage] = useState(Boolean(testimonial.image))
  const initials = testimonial.name.split(" ").map((n) => n[0]).join("")

  return (
    <div className="relative w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0 overflow-hidden">
      {hasImage && testimonial.image ? (
        <Image
          src={testimonial.image}
          alt={testimonial.name}
          fill
          sizes="40px"
          className="object-cover"
          onError={() => setHasImage(false)}
        />
      ) : (
        <span className="font-[family-name:var(--font-heading)] text-sm font-bold text-primary">
          {initials}
        </span>
      )}
    </div>
  )
}

export function TestimonialsSection() {
  return (
    <section id="referanslar" className="relative py-16 sm:py-24 lg:py-32" aria-label="Müşteri yorumları">
      <div className="brand-divider" />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-12 sm:pt-16 max-w-6xl">

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="text-center max-w-3xl mx-auto mb-12 sm:mb-16"
        >
          <motion.span variants={sectionLabel} className="text-primary font-medium text-sm uppercase tracking-wider inline-block">
            Müşteri Yorumları
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-[family-name:var(--font-heading)] text-3xl sm:text-4xl md:text-5xl font-bold mt-4 mb-4 sm:mb-6 text-foreground leading-tight">
            Müşterilerimiz <span className="text-gradient-brand">Ne Diyor?</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="text-muted-foreground text-base sm:text-lg leading-snug sm:leading-relaxed">
            Birlikte gerçekleştirdiğimiz projelerden müşterilerimizin deneyimleri.
          </motion.p>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={VP}
          className="flex overflow-x-auto snap-x snap-mandatory hide-scrollbar gap-4 pb-8 -mx-4 px-4 sm:mx-0 sm:px-0 sm:pb-0 sm:grid sm:grid-cols-2 lg:grid-cols-3 sm:gap-6"
        >
          {testimonials.map((t) => (
            <motion.div
              key={t.name}
              variants={staggerItem}
              className="glass hairline rounded-2xl p-6 sm:p-8 flex flex-col gap-4 group hover:glow-accent transition-shadow duration-300 min-w-[85vw] snap-center sm:min-w-0"
            >
              <Quote className="w-8 h-8 text-primary/30 shrink-0" aria-hidden="true" />
              <div className="flex gap-1" role="img" aria-label={`${t.rating} üzerinden 5 yıldız`}>
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 fill-primary text-primary" aria-hidden="true" />
                ))}
              </div>
              <p className="text-foreground text-sm sm:text-base leading-relaxed flex-1">
                &ldquo;{t.quote}&rdquo;
              </p>
              <div className="flex items-center gap-3 pt-2 border-t border-border/50">
                <TestimonialAvatar testimonial={t} />
                <div>
                  <p className="font-semibold text-foreground text-sm">{t.name}</p>
                  <p className="text-muted-foreground text-xs">{t.role}, {t.company}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

      </div>
    </section>
  )
}
